import { lazy, StrictMode, Suspense, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, NavLink, Outlet, RouterProvider, useNavigate } from "react-router";
import { lastServerResponseAt, reportClientError, setConnectivityHandler } from "./api/client";
import { RequireAuth, SessionProvider, useSession } from "./auth/session";
import { LoginPage } from "./features/auth/LoginPage";
import { PasswordDialog } from "./features/auth/PasswordDialog";
import { EventsPage } from "./features/events/EventsPage";
import { EventWorkspace } from "./features/events/EventWorkspace";
import { formatTime } from "./domain/format";
import { fr } from "./i18n/fr";
import { AppPage } from "./public-pages/AppPage";
import { JoinPage } from "./public-pages/JoinPage";
import { Logo, OfflineContext } from "./ui/components";
import "./ui/styles.css";

// heavy and less frequent screens are loaded on demand (SW-NF-03)
const AdminPage = lazy(() => import("./features/admin/AdminPage").then((m) => ({ default: m.AdminPage })));
const RouteTestPage = lazy(() => import("./features/route-test/RouteTestPage").then((m) => ({ default: m.RouteTestPage })));

// errors raised in the browser reach the server log (SW-NF-05)
window.addEventListener("error", (ev) => reportClientError(ev.message || String(ev.error), ev.error instanceof Error ? ev.error.stack : undefined));
window.addEventListener("unhandledrejection", (ev) => {
  const r: unknown = ev.reason;
  reportClientError(r instanceof Error ? `${r.name}: ${r.message}` : String(r), r instanceof Error ? r.stack : undefined);
});

function Shell() {
  const s = useSession();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [offline, setOffline] = useState(false);
  // instant of the last answer of the server: the banner says how old the shown data is (SW-SY-10)
  const [since, setSince] = useState<Date | null>(null);
  useEffect(() => {
    setConnectivityHandler((ok) => { setOffline((was) => { if (!ok && !was) setSince(lastServerResponseAt()); return !ok; }); });
    return () => setConnectivityHandler(null);
  }, []);
  const forced = !!s.user?.mustChangePassword;
  return (
    <OfflineContext.Provider value={offline}>
    <div className="shell">
      <header className="topbar">
        <NavLink to="/" className="brand"><Logo size={26} /> {fr.appName}</NavLink>
        <nav>
          <NavLink to="/" end>{fr.nav.events}</NavLink>
          <NavLink to="/test">{fr.nav.routeTest}</NavLink>
          {s.isAdmin && <NavLink to="/admin">{fr.nav.admin}</NavLink>}
          <NavLink to="/app">{fr.nav.app}</NavLink>
        </nav>
        {s.user && (
          <div className="user-menu">
            <button className="ghost" onClick={() => setPwOpen(true)} title={fr.nav.account}>{s.user.displayName}</button>
            <button className="ghost" onClick={async () => { await s.logout(); navigate("/login"); }}>{fr.nav.logout}</button>
          </div>
        )}
      </header>
      {offline && <div className="offline-banner" role="alert">{since ? fr.common.offlineSince(formatTime(since)) : fr.common.offline}</div>}
      <main><Suspense fallback={<div className="page muted">{fr.common.loadingModule}</div>}><Outlet /></Suspense></main>
      {s.user && <PasswordDialog open={pwOpen || forced} forced={forced} onClose={() => setPwOpen(false)} />}
    </div>
    </OfflineContext.Provider>
  );
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/", element: <RequireAuth><EventsPage /></RequireAuth> },
      { path: "/events/:id", element: <RequireAuth><EventWorkspace /></RequireAuth> },
      { path: "/events/:id/:tab", element: <RequireAuth><EventWorkspace /></RequireAuth> },
      { path: "/test", element: <RequireAuth permission="route.test"><RouteTestPage /></RequireAuth> },
      { path: "/admin", element: <RequireAuth permission="user.manage"><AdminPage /></RequireAuth> },
      { path: "/admin/:tab", element: <RequireAuth permission="user.manage"><AdminPage /></RequireAuth> },
      { path: "/app", element: <AppPage /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
  { path: "/join/:code", element: <JoinPage /> },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <SessionProvider>
      <RouterProvider router={router} />
    </SessionProvider>
  </StrictMode>,
);
